/** @fileoverview Renders reusable app components and layout containers. */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { ExternalLink, LoaderCircle } from 'lucide-react';
import { cn } from 'cn';

import { Button, buttonVariants } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

import type {
  FabrikButtonComponent,
  FabrikComponent,
  FabrikFieldsComponent,
  FabrikGridComponent,
  FabrikCardComponent,
  FabrikMetricComponent,
  FabrikStateComponent,
  FabrikTextComponent,
} from '../../types/apps';
import {
  useComponentState,
  useDataReader,
  useDataVersion,
  useFabrikGlobals,
  useValueContext,
} from './appRuntimeContext';
import { toODataCount } from '../../services/fabrikApi';
import { gridContainerProps, gridItemProps } from './componentLayout';
import ChartRenderer from './ChartRenderer';
import ChecklistRenderer from './ChecklistRenderer';
import {
  findStateComponent,
  formatDisplayValue,
  formatMetricCount,
  safeExternalUrl,
  SAVE_APP_NOTE,
  valueOf,
} from './helpers';
import { resolveLucideIconName } from '../../lib/lucideIcons';
import type { ValueContext } from './helpers';
import InlineAlert from './InlineAlert';
import ListRenderer, { readSourceQuery } from './ListRenderer';
import TimelineRenderer from './TimelineRenderer';
import type { ComponentRendererProps, FieldValues } from './types';
import {
  getActionSendRecordKeyRefs,
  getActionTargetIds,
  useComponentStateStore,
  useIsActionBlocked,
} from './componentStateStore';
import { useComponentStateRows } from './useComponentStateRows';
import ActionConfirmDialog from './ActionConfirmDialog';
import { appButtonLook, type AppButtonInput } from './appButton';
import { iconPixelsFor, typeStepFor, type TypeStep } from './componentSize';
import { useRunAction } from './useRunAction';
import { FormRenderer, StandaloneInput } from './InputRenderer';
import { openServerLink } from './openServerLink';

type ShowWhenGateProps = {
  showWhen: NonNullable<FabrikButtonComponent['showWhen']>;
  recordKey: FabrikStateComponent['recordKey'];
  fields: FieldValues;
  children: ReactNode;
  screenId: string;
  inputs: ComponentRendererProps['inputs'];
};

/**
 * Shows its children only when the named checklist or timeline has no rows, or any rows, as asked.
 * Hides them while the rows load or reload, so an action is not run twice by mistake, and shows a short
 * message in their place when the rows cannot be read.
 * In the builder before the app is saved, the rows cannot be read, so the children always show.
 * @param props the button's showWhen rule, the record fields, and the button to show
 */
const ShowWhenGate = ({
  showWhen,
  recordKey,
  fields,
  screenId,
  inputs,
  children,
}: ShowWhenGateProps) => {
  const { componentId, rows: rule } = showWhen;
  const { isAvailable, isLoading, rows, error } = useComponentStateRows(
    componentId,
    fields,
    recordKey,
    screenId,
    inputs
  );
  if (!isAvailable) {
    return <>{children}</>;
  }
  if (isLoading) {
    return null;
  }
  if (error) {
    return (
      <InlineAlert className="mt-1">Could not check this block. Reload to try again.</InlineAlert>
    );
  }
  const hasRows = rows.length > 0;
  return (rule === 'none' && !hasRows) || (rule === 'any' && hasRows) ? <>{children}</> : null;
};

type ActionButtonProps = {
  actionId: string;
  label: string;
  look: AppButtonInput;
  fields: FieldValues;
  screenComponents: FabrikComponent[];
  screenId: string;
  inputs: ComponentRendererProps['inputs'];
  selectionPath?: string;
};

/**
 * Button that runs an action for the record on screen, then reloads the checklists and timelines it writes to.
 * Hidden when the viewer is in none of the action's allowed groups.
 * @param props action id, button label, look and size, and the record fields
 */
const ActionButton = ({
  actionId,
  label,
  look,
  fields,
  screenComponents,
  screenId,
  inputs,
  selectionPath,
}: ActionButtonProps) => {
  const { actions } = useComponentStateStore();
  const isBlocked = useIsActionBlocked(actionId);
  const { canRun, isBusy, error, runAction, confirmation } = useRunAction();
  if (isBlocked) {
    return null;
  }
  const actionTargets = getActionTargetIds(actions, actionId).flatMap((componentId) => {
    const target = findStateComponent(screenComponents, componentId);
    return target ? [target] : [];
  });
  const recordKeyRefs = getActionSendRecordKeyRefs(actions, actionId, screenComponents);

  return (
    <div>
      <Button
        type="button"
        {...appButtonLook(look)}
        disabled={!canRun || isBusy}
        aria-busy={isBusy || undefined}
        title={canRun ? undefined : SAVE_APP_NOTE}
        onClick={() =>
          runAction({ actionId, screenId, fields, inputs, targets: actionTargets, recordKeyRefs })
        }
      >
        {isBusy && <LoaderCircle className="animate-spin" aria-hidden="true" />}
        <span data-fabrik-selection-path={selectionPath}>{label}</span>
      </Button>
      {error && <InlineAlert className="mt-1">{error}</InlineAlert>}
      <ActionConfirmDialog {...confirmation} />
    </div>
  );
};

type ServerLinkButtonProps = {
  label: string;
  look: AppButtonInput;
  screenId: string;
  componentId: string;
  fields: FieldValues;
  selectionPath?: string;
};

/** Button that opens a link whose address the server builds for the record on screen. */
const ServerLinkButton = ({
  label,
  look,
  screenId,
  componentId,
  fields,
  selectionPath,
}: ServerLinkButtonProps) => {
  const componentState = useComponentState();
  const [error, setError] = useState<string | null>(null);

  return (
    <div>
      <Button
        type="button"
        {...appButtonLook(look)}
        disabled={!componentState}
        title={componentState ? undefined : SAVE_APP_NOTE}
        onClick={() => {
          if (!componentState) {
            return;
          }
          setError(null);
          openServerLink({
            resolve: () => componentState.resolveLink({ screenId, componentId, fields }),
            onError: setError,
          });
        }}
      >
        <span data-fabrik-selection-path={selectionPath}>{label}</span>
        <ExternalLink aria-hidden="true" />
      </Button>
      {error && <InlineAlert className="mt-1">{error}</InlineAlert>}
    </div>
  );
};

type FieldsBlockProps = {
  component: FabrikFieldsComponent;
  fields: FieldValues;
  selectionPath?: string;
  isSidePanel?: boolean;
  valueContext: ValueContext;
};

/**
 * Shows each label over its value in a grid; two columns on wide pages, one in the side panel.
 * @param props the fields component, the record fields, whether it sits in the side panel, and the app's variables and globals
 */
/* In the narrow side panel each field is one row, label left and value right, to save height. */
const FieldsBlock = ({
  component,
  fields,
  selectionPath,
  isSidePanel,
  valueContext,
}: FieldsBlockProps) => (
  <dl
    className={
      isSidePanel
        ? 'flex flex-col divide-y divide-border'
        : 'grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2'
    }
  >
    {component.items.map((item, itemIndex) => {
      const itemPath = selectionPath ? `${selectionPath}/items/${itemIndex}` : undefined;
      return (
        <div
          key={itemIndex}
          data-fabrik-selection-path={itemPath}
          className={
            isSidePanel ? 'flex min-w-0 items-baseline justify-between gap-4 py-2' : 'min-w-0'
          }
        >
          <dt
            data-fabrik-selection-path={itemPath ? `${itemPath}/label` : undefined}
            className="shrink-0 type-body text-muted-foreground"
          >
            {item.label}
          </dt>
          <dd
            data-fabrik-selection-path={itemPath ? `${itemPath}/value` : undefined}
            className={`type-body wrap-anywhere text-card-foreground tabular-nums ${
              isSidePanel ? 'text-right' : 'mt-0.5'
            }`}
          >
            {formatDisplayValue(valueOf(item.value, fields, valueContext))}
          </dd>
        </div>
      );
    })}
  </dl>
);

type NestedRendererProps = Omit<ComponentRendererProps, 'component'>;

const GridBlock = ({
  component,
  selectionPath,
  ...childProps
}: NestedRendererProps & { component: FabrikGridComponent }) => (
  <div {...gridContainerProps(component)}>
    {component.items.map(({ component: child, ...look }, itemIndex) => {
      const itemPath = selectionPath ? `${selectionPath}/items/${itemIndex}` : undefined;
      const childPath = itemPath ? `${itemPath}/component` : undefined;
      return (
        <div
          key={child.id}
          data-fabrik-selection-path={itemPath}
          {...gridItemProps(component.mode, look)}
        >
          <ComponentRenderer component={child} {...childProps} selectionPath={childPath} />
        </div>
      );
    })}
  </div>
);

const CardBlock = ({
  component,
  selectionPath,
  ...childProps
}: NestedRendererProps & { component: FabrikCardComponent }) => (
  /* h-full lets cards side by side in a grid row share one height. */
  <Card className="h-full min-w-0 gap-0 py-0">
    {(component.title || component.note) && (
      <CardHeader className="flex flex-row items-baseline justify-between gap-3 border-b px-4 py-3">
        {component.title && (
          <h3
            data-fabrik-selection-path={selectionPath ? `${selectionPath}/title` : undefined}
            className="type-body font-semibold text-card-foreground"
          >
            {component.title}
          </h3>
        )}
        {component.note && (
          <p
            data-fabrik-selection-path={selectionPath ? `${selectionPath}/note` : undefined}
            className="type-caption text-muted-foreground"
          >
            {component.note}
          </p>
        )}
      </CardHeader>
    )}
    <CardContent className="min-w-0 px-4 py-4">
      <ComponentRenderer
        component={component.content}
        {...childProps}
        selectionPath={selectionPath ? `${selectionPath}/content` : undefined}
      />
    </CardContent>
  </Card>
);

const MetricBlock = ({
  component,
  fields,
  inputs,
  selectionPath,
}: {
  component: FabrikMetricComponent;
  fields: FieldValues;
  inputs: ComponentRendererProps['inputs'];
  selectionPath?: string;
}) => {
  const readData = useDataReader();
  const dataVersion = useDataVersion();
  const globals = useFabrikGlobals();
  const [state, setState] = useState<{ count?: number | null; loading: boolean; error: boolean }>({
    loading: true,
    error: false,
  });
  useEffect(() => {
    if (component.dataSource.type === 'items') {
      setState({ count: component.dataSource.items.length, loading: false, error: false });
      return;
    }
    const controller = new AbortController();
    setState({ loading: true, error: false });
    readData({
      dataSource: readSourceQuery(component.dataSource, fields, globals, inputs),
      options: { count: true },
      signal: controller.signal,
    })
      .then((response) => {
        if (!controller.signal.aborted)
          setState({ count: toODataCount(response), loading: false, error: false });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ loading: false, error: true });
      });
    return () => controller.abort();
  }, [component.dataSource, dataVersion, fields, globals, inputs, readData]);
  return (
    <div className="min-w-0">
      <p
        data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}
        className="type-small font-medium text-muted-foreground"
      >
        {component.label}
      </p>
      {/* Big counts show a short form; the exact number is in the tooltip. */}
      <p
        data-fabrik-selection-path={selectionPath ? `${selectionPath}/runtime/count` : undefined}
        data-fabrik-selection-definition-path={
          selectionPath ? `${selectionPath}/dataSource` : undefined
        }
        className={`mt-1 ${typeStepFor(
          'type-display',
          component.size
        )} font-semibold tracking-tight tabular-nums`}
        aria-live="polite"
        title={typeof state.count === 'number' ? state.count.toLocaleString() : undefined}
      >
        {state.loading ? (
          <>
            <Skeleton className="inline-block h-8 w-16 align-middle" aria-hidden="true" />
            <span className="sr-only">Loading…</span>
          </>
        ) : state.error || state.count === null || state.count === undefined ? (
          '—'
        ) : (
          formatMetricCount(state.count)
        )}
      </p>
      {component.supportingText && (
        <p
          data-fabrik-selection-path={selectionPath ? `${selectionPath}/supportingText` : undefined}
          className="mt-1 type-caption text-muted-foreground"
        >
          {component.supportingText}
        </p>
      )}
      {(state.error || (!state.loading && state.count === null)) && (
        <p className="mt-1 type-caption text-destructive" role="status">
          Count unavailable
        </p>
      )}
    </div>
  );
};

/* Element, usual text step and look of each text variant; the size step moves the text step. */
const TEXT_LOOKS: Record<
  FabrikTextComponent['style']['variant'],
  { tag: 'h1' | 'h2' | 'h3' | 'p'; step: TypeStep; className: string }
> = {
  display: {
    tag: 'h1',
    step: 'type-display',
    className: 'font-semibold tracking-tight text-foreground',
  },
  eyebrow: {
    tag: 'p',
    step: 'type-caption',
    className: 'font-semibold tracking-wider text-primary uppercase',
  },
  heading: {
    tag: 'h2',
    step: 'type-heading',
    className: 'font-semibold tracking-tight text-foreground',
  },
  subheading: { tag: 'h3', step: 'type-body', className: 'font-semibold text-foreground' },
  note: { tag: 'p', step: 'type-small', className: 'text-muted-foreground' },
  body: { tag: 'p', step: 'type-body', className: 'max-w-prose text-card-foreground' },
};

/**
 * Renders one app component for the current screen.
 * @param props the component, the record fields, the navigation callback, the selected list item,
 *   and whether it sits in the side panel
 */
function ComponentRendererBody({
  component,
  screenComponents,
  fields,
  screenId,
  inputs,
  commitStandaloneInput,
  setInputs,
  onOpenTarget,
  selectedItemId,
  selectionPath,
  isSidePanel,
}: ComponentRendererProps) {
  const valueContext = { ...useValueContext(), inputs };

  const nestedProps = {
    screenComponents,
    screenId,
    fields,
    inputs,
    commitStandaloneInput,
    setInputs,
    onOpenTarget,
    selectedItemId,
    selectionPath,
    isSidePanel,
  };
  if (component.kind === 'grid') return <GridBlock component={component} {...nestedProps} />;
  if (component.kind === 'card') return <CardBlock component={component} {...nestedProps} />;
  if (component.kind === 'metric')
    return (
      <MetricBlock
        component={component}
        fields={fields}
        inputs={inputs}
        selectionPath={selectionPath}
      />
    );
  if (component.kind === 'chart')
    return <ChartRenderer component={component} selectionPath={selectionPath} />;
  if (component.kind === 'icon') {
    const name = resolveLucideIconName(component.name.trim());
    if (!name) return null;
    return (
      <DynamicIcon
        data-fabrik-selection-path={selectionPath ? `${selectionPath}/name` : undefined}
        name={name}
        size={iconPixelsFor(component.size)}
        aria-hidden={!component.label || undefined}
        aria-label={component.label}
        role={component.label ? 'img' : undefined}
      />
    );
  }

  if (component.kind === 'input') {
    return (
      <StandaloneInput
        input={component}
        screenId={screenId}
        fields={fields}
        inputs={inputs}
        commitStandaloneInput={commitStandaloneInput}
        selectionPath={selectionPath}
      />
    );
  }

  if (component.kind === 'form') {
    return (
      <FormRenderer
        component={component}
        screenId={screenId}
        screenComponents={screenComponents}
        fields={fields}
        inputs={inputs}
        setInputs={setInputs}
        onOpenTarget={onOpenTarget}
        selectionPath={selectionPath}
      />
    );
  }

  if (component.kind === 'fields') {
    return (
      <FieldsBlock
        component={component}
        fields={fields}
        selectionPath={selectionPath}
        isSidePanel={isSidePanel}
        valueContext={valueContext}
      />
    );
  }

  if (component.kind === 'text') {
    const text = valueOf(component.text, fields, valueContext);
    const { tag: Tag, step, className } = TEXT_LOOKS[component.style.variant];
    return (
      <Tag
        data-fabrik-selection-path={selectionPath ? `${selectionPath}/text` : undefined}
        className={`${typeStepFor(step, component.size)} ${className}`}
      >
        {text}
      </Tag>
    );
  }

  if (component.kind === 'checklist') {
    return (
      <ChecklistRenderer
        component={component}
        screenId={screenId}
        fields={fields}
        inputs={inputs}
        screenComponents={screenComponents}
        selectionPath={selectionPath}
      />
    );
  }

  if (component.kind === 'timeline') {
    return (
      <TimelineRenderer
        component={component}
        screenId={screenId}
        fields={fields}
        inputs={inputs}
        selectionPath={selectionPath}
      />
    );
  }

  if (component.kind === 'button') {
    const { style, target, showWhen, size } = component;
    const look = { variant: style.variant, size };
    const label = valueOf(component.label, fields, valueContext);

    let button: ReactNode;
    if (target.type === 'action') {
      button = (
        <ActionButton
          actionId={target.actionId}
          label={label}
          look={look}
          fields={fields}
          screenComponents={screenComponents}
          screenId={screenId}
          inputs={inputs}
          selectionPath={selectionPath ? `${selectionPath}/label` : undefined}
        />
      );
    } else if (target.type === 'link' && typeof target.url === 'string') {
      const url = safeExternalUrl(target.url);
      button = url ? (
        <a
          className={cn(buttonVariants(appButtonLook(look)))}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}>
            {label}
          </span>
          <ExternalLink aria-hidden="true" />
        </a>
      ) : null;
    } else if (target.type === 'link') {
      button = (
        <ServerLinkButton
          label={label}
          look={look}
          screenId={screenId}
          componentId={component.id}
          fields={fields}
          selectionPath={selectionPath ? `${selectionPath}/label` : undefined}
        />
      );
    } else {
      button = (
        <Button type="button" {...appButtonLook(look)} onClick={() => onOpenTarget(target, fields)}>
          <span data-fabrik-selection-path={selectionPath ? `${selectionPath}/label` : undefined}>
            {label}
          </span>
        </Button>
      );
    }

    const showWhenTarget = showWhen
      ? findStateComponent(screenComponents, showWhen.componentId)
      : undefined;
    return showWhen && button ? (
      <ShowWhenGate
        showWhen={showWhen}
        recordKey={showWhenTarget?.recordKey ?? []}
        screenId={screenId}
        fields={fields}
        inputs={inputs}
      >
        {button}
      </ShowWhenGate>
    ) : (
      button
    );
  }

  return (
    <ListRenderer
      component={component}
      screenComponents={screenComponents}
      screenId={screenId}
      fields={fields}
      inputs={inputs}
      onOpenTarget={onOpenTarget}
      selectedItemId={selectedItemId}
      selectionPath={selectionPath}
    />
  );
}

export default function ComponentRenderer(props: ComponentRendererProps) {
  const content = <ComponentRendererBody {...props} />;
  return props.selectionPath ? (
    /* h-full keeps a card stretched by its grid cell from stopping at this wrapper. */
    <div className="h-full min-w-0" data-fabrik-selection-path={props.selectionPath}>
      {content}
    </div>
  ) : (
    content
  );
}
